'use client';

import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import RecordAgentPanel from '../../components/crm/RecordAgentPanel';
import Tabs from '../../components/ui/Tabs';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { CustomerFinancePanel } from '../../components/crm/billing/CustomerFinancePanel';
import {
  User, Mail, Phone, Edit2, Save, X, ArrowLeft, Star,
  Briefcase, Activity, TrendingUp, Plus, CheckCircle,
  Clock, FileText, Zap, AlertCircle, MapPin, LinkedinIcon,
  Loader, Eye, ExternalLink, MessageSquare, Shield, Crosshair, Award, Hash, Building2,
  ChevronRight, ArrowUpRight, ShieldCheck, Info, Fingerprint, PhoneCall, ListChecks, Receipt, Download, Share2, UserCog, History, Instagram, Facebook
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatDate } from '../../utils/formatDate';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import ApplyTemplateModal from '../../components/crm/ApplyTemplateModal';
import ShareModal from '../../components/crm/ShareModal';
import TransferOwnershipModal from '../../components/crm/TransferOwnershipModal';
import LinkInstagramModal from '../../components/crm/LinkInstagramModal';
import UnifiedTimeline from '../../components/crm/UnifiedTimeline';
import RelatedFiles from '../../components/crm/RelatedFiles';
import FormActivityPanel from '../../components/crm/FormActivityPanel';
import { downloadInvoicePdf, previewInvoicePdf } from '../../utils/invoicePdf';
import { PageLoader } from '../../components/crm/ui';

const TABS = [
  { id: 'overview', label: 'Details', icon: User },
  { id: 'activities', label: 'Activity History', icon: Activity },
  { id: 'deals', label: 'Deals & Revenue', icon: TrendingUp },
  { id: 'tasks', label: 'Tasks', icon: ListChecks },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'finance', label: 'Finance', icon: Receipt },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'formActivity', label: 'Form Activity', icon: FileText },
  { id: 'history', label: 'Timeline', icon: History },
];

export default function ContactDetailPage() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const baseCurrency = useCompanyCurrency();

  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pdfActionId, setPdfActionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [callingAgent, setCallingAgent] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showLinkInstagram, setShowLinkInstagram] = useState(false);
  const [showLinkFacebook, setShowLinkFacebook] = useState(false);
  const [accountContacts, setAccountContacts] = useState([]);

  useEffect(() => { fetchContactData(); }, [contactId]);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      const [contactRes, invoicesRes] = await Promise.all([
        api.get(`/crm/contacts/${contactId}`),
        api.get('/crm/invoices', { params: { contactId } }),
      ]);
      if (contactRes.data.success) {
        setContact(contactRes.data.contact);
        setActivities(contactRes.data.activities || []);
        setDeals(contactRes.data.deals || []);
        setLeads(contactRes.data.leads || []);
        setTasks(contactRes.data.tasks || []);
        setFormData(contactRes.data.contact);
        // "Reports to" only makes sense against other contacts under the same
        // account — fetch siblings once we know it, so the picker doesn't
        // offer unrelated org's contacts.
        if (contactRes.data.contact.accountId) {
          const siblingsRes = await api.get('/crm/contacts', { params: { accountId: contactRes.data.contact.accountId, limit: 100 } });
          setAccountContacts((siblingsRes.data.contacts || []).filter(c => c._id !== contactId));
        }
      } else setError('Could not load record.');
      setInvoices(invoicesRes.data.invoices || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvoice = async (invoice) => {
    setPdfActionId(invoice._id + ':preview');
    await previewInvoicePdf(invoice);
    setPdfActionId(null);
  };

  const handleDownloadInvoice = async (invoice) => {
    setPdfActionId(invoice._id + ':download');
    await downloadInvoicePdf(invoice);
    setPdfActionId(null);
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await api.put(`/crm/contacts/${contactId}`, formData);
      setContact(formData);
      setIsEditing(false);
      alertService.success('Success', 'Contact saved');
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to update record');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  if (!contact) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-[#F4F6F9] rounded-full flex items-center justify-center mx-auto text-[#706E6B]">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-[#080707]">Record Not Found</h2>
        <button onClick={() => navigate('/dashboard/crm/contacts')} className="text-[#0176D3] font-bold text-sm hover:underline flex items-center justify-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Return to Contacts
        </button>
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-6 max-w-[100rem] mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumb className="mb-2" items={[{ label: 'Contacts', to: '/dashboard/crm/contacts' }, { label: contact.name }]} />
      <RecordAgentPanel entityType="contact" entityId={contactId} />

      {/* Profile Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-[#0176D3] flex items-center justify-center text-white font-bold ">
              {contact.name?.slice(0, 1).toUpperCase()}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-lg sm:text-xl font-bold text-[#2E2E2E]">{isEditing ? 'Editing Profile' : contact.name}</h1>
                <span className="sf-badge sf-badge-neutral">CONTACT</span>
                {contact.lifecycleStage === 'customer' && <span className="sf-badge sf-badge-success">CUSTOMER</span>}
              </div>
              <div className="flex items-center gap-4 text-[12px] text-[#706E6B] font-medium">
                <span className="flex items-center gap-1.5"><Briefcase size={12} /> {contact.designation || 'Key Decision Maker'}</span>
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#0176D3]" onClick={() => contact.accountId && navigate(`/dashboard/crm/accounts/${contact.accountId}`)}><Building2 size={12} /> {contact.company || 'No company'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F3F3] whitespace-nowrap shrink-0">Cancel</button>
                <button onClick={handleSave} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] whitespace-nowrap shrink-0">Save</button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Edit2 size={14} /> Edit
                </button>
                <button onClick={() => setShowApplyTemplate(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <ListChecks size={14} /> Apply Template
                </button>
                <button className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <MessageSquare size={14} /> Send WhatsApp
                </button>
                <button onClick={() => setShowShareModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={() => setShowTransferModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <UserCog size={14} /> Transfer
                </button>
                {contact.instagramId ? (
                  <span className="h-9 px-4 border border-[#D8DDE6] rounded text-[13px] font-bold flex items-center gap-2 text-[#706E6B]">
                    <Instagram size={14} className="text-[#E1306C]" /> IG Linked
                  </span>
                ) : (
                  <button onClick={() => setShowLinkInstagram(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                    <Instagram size={14} /> Link Instagram
                  </button>
                )}
                {contact.facebookPsid ? (
                  <span className="h-9 px-4 border border-[#D8DDE6] rounded text-[13px] font-bold flex items-center gap-2 text-[#706E6B]">
                    <Facebook size={14} className="text-[#1877F2]" /> FB Linked
                  </span>
                ) : (
                  <button onClick={() => setShowLinkFacebook(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                    <Facebook size={14} /> Link Facebook
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Highlight Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Email</p>
            <p className="text-[13px] font-medium text-[#0176D3] truncate">{contact.email || 'N/A'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Phone</p>
            <p className="text-[13px] font-medium text-[#080707] tabular-nums">{contact.phoneNumber || 'N/A'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Department</p>
            <p className="text-[13px] font-medium text-[#080707]">{contact.department || 'External'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Last Sync</p>
            <p className="text-[13px] font-medium text-[#706E6B] italic">{formatDate(contact.updatedAt)}</p>
          </div>
        </div>

        {/* Ownership & Audit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Record Owner</p>
            <p className="text-[13px] font-medium text-[#080707]">{contact.ownerName || 'Unassigned'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Created By</p>
            <p className="text-[13px] font-medium text-[#080707]">{contact.createdByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Last Modified By</p>
            <p className="text-[13px] font-medium text-[#080707]">{contact.updatedByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Modified On</p>
            <p className="text-[13px] font-medium text-[#706E6B]">{contact.updatedAt ? formatDate(contact.updatedAt) : '—'}</p>
          </div>
        </div>
      </div>

      {/* Tabs Control */}
      <Tabs tabs={TABS.map((t) => ({ ...t, key: t.id }))} value={activeTab} onChange={setActiveTab} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {activeTab === 'overview' && (
            <div className="sf-card p-8 bg-white space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Professional Designation</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{contact.designation || 'Not Specified'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Department / Sector</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{contact.department || 'MANAGEMENT'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">LinkedIn Handle</label>
                  <div className="flex items-center gap-2 group cursor-pointer text-[#0176D3] font-medium text-[13px]">
                     {contact.customFields?.linkedIn || 'N/A'} <LinkedinIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Address / City</label>
                  <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{contact.city || 'GLOBAL'}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Reports To</label>
                  {isEditing ? (
                    <select
                      className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                      value={formData.reportsToId || ''}
                      onChange={e => setFormData({ ...formData, reportsToId: e.target.value || null })}
                      disabled={accountContacts.length === 0}
                    >
                      <option value="">No manager</option>
                      {accountContacts.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">
                      {accountContacts.find(c => c._id === contact.reportsToId)?.name || 'No manager'}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Visibility</label>
                  {isEditing ? (
                    <select className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" value={formData.visibility || 'team'} onChange={e => setFormData({...formData, visibility: e.target.value})}>
                      <option value="team">Team — everyone can see this</option>
                      <option value="private">Private — only owner and shared users</option>
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{contact.visibility === 'private' ? 'Private' : 'Team'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Bio</label>
                <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] text-[13px] text-[#080707] min-h-[100px] shadow-inner font-medium">
                  {contact.notes || 'No historical intelligence bio recorded for this contact node.'}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {activities.length > 0 ? activities.slice(0, 10).map(a => (
                <div key={a._id} className="sf-card p-4 flex gap-4 hover:border-[#0176D3] transition-all">
                  <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
                    <Activity size={18} className="text-[#0176D3]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-[14px] font-bold text-[#080707]">{a.title}</h4>
                      <span className="text-[11px] font-bold text-[#706E6B]">{formatDate(a.createdAt)}</span>
                    </div>
                    <p className="text-[12px] text-[#706E6B] line-clamp-2">{a.description}</p>
                  </div>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Activity size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Registry Silent / No activity</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'deals' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-bottom-4">
              {deals.length > 0 ? deals.map(d => (
                <div key={d._id} className="sf-card p-6 space-y-4 hover:border-[#0176D3] transition-all group">
                  <div className="flex justify-between items-start">
                    <h4 className="text-[15px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">{d.title}</h4>
                    <span className="sf-badge sf-badge-success">{formatCurrency(d.value, d.currency || baseCurrency)}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-[11px] font-bold text-[#706E6B]">
                      <span className="uppercase tracking-widest">Confidence</span>
                      <span>{d.probability}%</span>
                    </div>
                    <div className="h-2 w-full bg-[#F4F6F9] rounded-full overflow-hidden border border-[#D8DDE6]">
                      <div className="h-full bg-[#0176D3]" style={{ width: `${d.probability}%` }} />
                    </div>
                  </div>
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Status: <span className="text-[#080707]">{d.status}</span></p>
                </div>
              )) : (
                <div className="col-span-full py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <TrendingUp size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Revenue Path Silent</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-3 animate-in slide-in-from-bottom-4">
              {tasks.length > 0 ? tasks.map(t => (
                <div
                  key={t._id}
                  onClick={() => navigate(`/dashboard/crm/tasks/${t._id}`)}
                  className="sf-card p-4 flex items-center justify-between hover:border-[#0176D3] transition-all cursor-pointer bg-white"
                >
                  <div>
                    <h4 className="text-[14px] font-bold text-[#080707]">{t.title}</h4>
                    <p className="text-[11px] text-[#706E6B] uppercase tracking-wider mt-1">{t.type}</p>
                  </div>
                  <span className="text-[11px] font-bold text-[#706E6B]">{t.dueDate ? formatDate(t.dueDate) : 'No due date'}</span>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <ListChecks size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Open Tasks</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'finance' && <CustomerFinancePanel contactId={contactId} name={contact?.name || [contact?.firstName, contact?.lastName].filter(Boolean).join(' ')} email={contact?.email} />}
          {activeTab === 'invoices' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {invoices.length > 0 ? (
                <div className="sf-card overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-[#F4F6F9] border-b border-[#D8DDE6]">
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Invoice #</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Amount</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Status</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Due Date</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D8DDE6]">
                      {invoices.map(inv => (
                        <tr key={inv._id} className="hover:bg-[#F7F9FB] transition-colors">
                          <td className="px-4 py-2.5 font-bold text-[#080707]">{inv.invoiceNumber}</td>
                          <td className="px-4 py-2.5 font-bold">{formatCurrency(inv.totalAmount, inv.currency)}</td>
                          <td className="px-4 py-2.5"><span className="sf-badge sf-badge-neutral">{inv.status.toUpperCase()}</span></td>
                          <td className="px-4 py-2.5 text-[12px] text-[#706E6B]">{new Date(inv.dueDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handlePreviewInvoice(inv)}
                                disabled={pdfActionId === inv._id + ':preview'}
                                className="text-[#0176D3] hover:underline flex items-center gap-1 text-[12px] font-bold disabled:opacity-50"
                              >
                                {pdfActionId === inv._id + ':preview' ? <Loader size={12} className="animate-spin" /> : <Eye size={12} />} Preview
                              </button>
                              <button
                                onClick={() => handleDownloadInvoice(inv)}
                                disabled={pdfActionId === inv._id + ':download'}
                                className="text-[#0176D3] hover:underline flex items-center gap-1 text-[12px] font-bold disabled:opacity-50"
                              >
                                {pdfActionId === inv._id + ':download' ? <Loader size={12} className="animate-spin" /> : <Download size={12} />} Download
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Receipt size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Invoices Yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <RelatedFiles module="contacts" moduleId={contactId} recordName={contact?.name} />
            </div>
          )}

          {activeTab === 'formActivity' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <FormActivityPanel recordType="contact" recordId={contactId} />
            </div>
          )}

          {activeTab === 'history' && (
            <UnifiedTimeline activities={activities} tasks={tasks} />
          )}
        </div>

        {/* Sidebar Info */}
        <div className="lg:col-span-4 space-y-6">
          <div className="sf-card p-6 bg-white space-y-6">
            <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3 mb-4">Account Relationship</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Company</span>
                <span className="text-[12px] font-bold text-[#0176D3] hover:underline cursor-pointer" onClick={() => contact.accountId && navigate(`/dashboard/crm/accounts/${contact.accountId}`)}>
                  {contact.company || 'No company'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Lifecycle Stage</span>
                <span className="sf-badge sf-badge-success">{(contact.lifecycleStage || 'lead').toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Key Influencer</span>
                <span className="text-[10px] font-bold text-[#2E844A] flex items-center gap-1"><Award size={12} /> DECISION MAKER</span>
              </div>
            </div>

            {leads.length > 0 && (
              <div className="pt-4 border-t border-[#D8DDE6] space-y-2">
                <h4 className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Related Leads</h4>
                {leads.slice(0, 5).map(l => (
                  <div key={l._id} className="flex items-center justify-between py-1.5">
                    <span
                      className="text-[13px] font-bold text-[#0176D3] cursor-pointer hover:underline truncate"
                      onClick={() => navigate(`/dashboard/crm/leads/${l._id}`)}
                    >
                      {l.name}
                    </span>
                    <span className="sf-badge sf-badge-neutral shrink-0">{l.status}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 space-y-3">
              <a
                href={contact.phoneNumber ? `tel:${contact.phoneNumber}` : undefined}
                className={cn(
                  "w-full h-11 bg-[#F4F6F9] border border-[#D8DDE6] text-[#080707] rounded font-bold text-[13px] hover:bg-white transition-all flex items-center justify-center gap-2 shadow-sm",
                  !contact.phoneNumber && "opacity-50 pointer-events-none"
                )}
              >
                <Phone size={14} /> Log Direct Call
              </a>
              <a
                href={contact.email ? `mailto:${contact.email}` : undefined}
                className={cn(
                  "w-full h-11 bg-[#0176D3] text-white rounded font-bold text-[13px] hover:bg-[#014486] transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex items-center justify-center gap-2",
                  !contact.email && "opacity-50 pointer-events-none"
                )}
              >
                <Mail size={14} /> Dispatch Email
              </a>
            </div>
          </div>

          <div className="sf-card p-6 bg-[#F4F6F9] border border-dashed border-[#D8DDE6] space-y-2">
             <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest flex items-center gap-2"><Fingerprint size={14} className="text-[#0176D3]" /> Unique Identifier</p>
             <p className="text-[9px] text-[#706E6B] font-mono break-all">{contact._id?.toUpperCase()}</p>
          </div>
        </div>
      </div>

      <ApplyTemplateModal
        isOpen={showApplyTemplate}
        onClose={() => setShowApplyTemplate(false)}
        contactId={contact._id}
      />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="contact"
        resourceId={contact._id}
        resourceLabel="Contact"
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        resourceType="contact"
        resourceId={contact._id}
        currentOwnerId={contact.ownerId}
        onTransferred={() => fetchContactData()}
      />
      <LinkInstagramModal
        isOpen={showLinkInstagram}
        onClose={() => setShowLinkInstagram(false)}
        contactId={contact._id}
        onLinked={() => fetchContactData()}
      />
      <LinkInstagramModal
        channel="facebook"
        isOpen={showLinkFacebook}
        onClose={() => setShowLinkFacebook(false)}
        contactId={contact._id}
        onLinked={() => fetchContactData()}
      />
    </div>
  );
}
